import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VoiceService, VoiceCall, CallHistoryEntry, VoicemailEntry } from 'app/core/services/voice.service';
import { VideoCallService, VideoCallState } from 'app/core/services/video-call.service';
import { ContactSearchService, EmailContact } from 'app/modules/admin/mailbox/contact-search.service';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { Subject, takeUntil, interval, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
    selector: 'voice-calls',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        DatePipe,
        OverlayModule,
        PortalModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <!--
            Trigger pill.

            Only renders when the logged-in user has a Twilio number
            assigned (voiceNumber). If the config call hasnt come back
            yet, or the user has no number at all, the entire pill is
            hidden — including the surrounding wrapper, which collapses
            because the component host uses display:contents and the
            parent .topbar-calls-pill has no intrinsic width of its own.

            The label is the formatted Twilio number itself rather
            than a generic "Calls" word — gives every workstation a
            visible reminder of which line they are answering on.
        -->
        @if (videoCallState.state === 'incoming' && videoCallState.session) {
            <div class="voice-calls-pill voice-calls-pill--video">
                <mat-icon class="voice-calls-pill__icon" [svgIcon]="'heroicons_outline:video-camera'"></mat-icon>
                <span class="voice-calls-pill__video-copy">
                    <strong>{{ videoCallState.session.caller.name }}</strong>
                    <small>Order #{{ videoCallState.session.order_id }}</small>
                </span>
                <button
                    type="button"
                    class="voice-calls-pill__video-action is-decline"
                    (click)="declineVideoCall()"
                    matTooltip="Decline video call"
                >
                    <mat-icon>call_end</mat-icon>
                </button>
                <button
                    type="button"
                    class="voice-calls-pill__video-action is-answer"
                    (click)="answerVideoCall()"
                    matTooltip="Answer video call"
                >
                    <mat-icon>videocam</mat-icon>
                </button>
            </div>
        } @else if (voiceNumber) {
            <!--
                Trigger pill.

                Rendered as a plain button with class voice-calls-pill
                rather than mat-icon-button because mat-icon-button
                hard-codes a 48 x 48 square footprint via the
                state-layer-size CSS variable plus an aspect-ratio
                reset that fought every override we threw at it from
                the layout-level stylesheet. By owning the trigger
                element's class ourselves we can style the pill in
                this component's scoped stylesheet below and guarantee
                the rule wins without an important-flag war.
            -->
            <button
                type="button"
                class="voice-calls-pill"
                #voiceCallsOrigin
                (click)="openPanel()"
                [matTooltip]="'Voice Calls · ' + formatPhoneNumber(voiceNumber) + (sharedLine ? ' (shared line)' : '')"
            >
                @if (missedCallCount + unreadVoicemailCount > 0) {
                    <span class="voice-calls-pill__badge voice-calls-pill__badge--count">
                        {{ missedCallCount + unreadVoicemailCount }}
                    </span>
                }
                @if (activeCall || incomingCall) {
                    <span class="voice-calls-pill__badge voice-calls-pill__badge--pulse"
                          [class.is-active]="activeCall"
                          [class.is-incoming]="incomingCall && !activeCall">
                    </span>
                }
                <mat-icon class="voice-calls-pill__icon" [svgIcon]="'heroicons_outline:phone'"></mat-icon>
                <span class="voice-calls-pill__number">{{ formatPhoneNumber(voiceNumber) }}</span>
            </button>
        }

        <!-- Voice calls panel -->
        <ng-template #voiceCallsPanel>
            <div class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl">
                <!-- Header -->
                <div class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary">
                    <div class="-ml-1 mr-3 sm:hidden">
                        <button mat-icon-button (click)="closePanel()">
                            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                        </button>
                    </div>
                    <mat-icon class="mr-2 icon-size-5" [svgIcon]="'heroicons_outline:phone'"></mat-icon>
                    <div class="flex flex-col leading-tight">
                        <div class="text-lg font-medium">Voice Calls</div>
                        @if (voiceNumber) {
                            <div class="text-xs opacity-80">
                                {{ formatPhoneNumber(voiceNumber) }}@if (sharedLine) { <span class="opacity-90">· Shared line</span> }
                            </div>
                        }
                    </div>
                    @if (isRegistered) {
                        <span class="ml-2 h-2 w-2 rounded-full bg-green-400" matTooltip="Connected"></span>
                    }
                </div>

                <!-- Tabs -->
                <div class="flex border-b border-gray-200 dark:border-gray-700 bg-card">
                    <button 
                        class="flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors"
                        [class.border-primary]="activeTab === 'calls'"
                        [class.text-primary]="activeTab === 'calls'"
                        [class.border-transparent]="activeTab !== 'calls'"
                        [class.text-secondary]="activeTab !== 'calls'"
                        (click)="activeTab = 'calls'">
                        Calls
                    </button>
                    <button 
                        class="flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors relative"
                        [class.border-primary]="activeTab === 'voicemails'"
                        [class.text-primary]="activeTab === 'voicemails'"
                        [class.border-transparent]="activeTab !== 'voicemails'"
                        [class.text-secondary]="activeTab !== 'voicemails'"
                        (click)="activeTab = 'voicemails'">
                        Voicemails
                        @if (unreadVoicemailCount > 0) {
                            <span class="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-medium text-white">
                                {{ unreadVoicemailCount }}
                            </span>
                        }
                    </button>
                </div>

                <!-- Content -->
                <div class="bg-card relative flex flex-auto flex-col overflow-y-auto sm:max-h-120">
                    
                    <!-- Incoming Call Alert (always visible) -->
                    @if (incomingCall) {
                        <div class="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                            <div class="flex items-center gap-3">
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-500 animate-pulse">
                                    <mat-icon class="text-white icon-size-5" [svgIcon]="'heroicons_solid:phone'"></mat-icon>
                                </div>
                                <div class="flex-1">
                                    <div class="font-semibold text-yellow-800 dark:text-yellow-200">Incoming Call</div>
                                    @if (incomingCall.callerName) {
                                        <div class="text-sm font-medium text-yellow-700 dark:text-yellow-300">{{ incomingCall.callerName }}</div>
                                    }
                                    <div class="text-sm text-yellow-600 dark:text-yellow-400">{{ formatPhoneNumber(incomingCall.from) }}</div>
                                </div>
                            </div>
                            <div class="flex gap-2 mt-3">
                                <button mat-flat-button color="primary" class="flex-1" (click)="answerCall()">
                                    <mat-icon class="icon-size-4 mr-1" [svgIcon]="'heroicons_solid:phone'"></mat-icon>
                                    Answer
                                </button>
                                <button mat-flat-button color="warn" class="flex-1" (click)="declineCall()">
                                    <mat-icon class="icon-size-4 mr-1" [svgIcon]="'heroicons_solid:phone-x-mark'"></mat-icon>
                                    Decline
                                </button>
                            </div>
                        </div>
                    }
                    
                    <!-- Active Call (always visible) -->
                    @if (activeCall) {
                        <div class="p-4 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
                            <div class="flex items-center gap-3">
                                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-green-500">
                                    <mat-icon class="text-white icon-size-5" [svgIcon]="'heroicons_solid:phone'"></mat-icon>
                                </div>
                                <div class="flex-1">
                                    <div class="font-semibold text-green-800 dark:text-green-200">
                                        {{ activeCall.direction === 'outgoing' ? 'Calling' : 'On Call' }}
                                    </div>
                                    @if (activeCall.callerName) {
                                        <div class="text-sm font-medium text-green-700 dark:text-green-300">{{ activeCall.callerName }}</div>
                                    }
                                    <div class="text-sm text-green-600 dark:text-green-400">
                                        {{ formatPhoneNumber(activeCall.direction === 'outgoing' ? activeCall.to : activeCall.from) }}
                                    </div>
                                </div>
                                <div class="text-sm font-mono text-green-700 dark:text-green-300">{{ callDuration }}</div>
                            </div>
                            <div class="flex gap-2 mt-3">
                                <button mat-stroked-button class="flex-1" (click)="toggleMute()" [class.bg-gray-200]="activeCall.isMuted">
                                    <mat-icon class="icon-size-4 mr-1">{{ activeCall.isMuted ? 'mic_off' : 'mic' }}</mat-icon>
                                    {{ activeCall.isMuted ? 'Unmute' : 'Mute' }}
                                </button>
                                <button mat-stroked-button (click)="showDialpad = !showDialpad" [class.bg-gray-200]="showDialpad"
                                        matTooltip="Keypad">
                                    <mat-icon class="icon-size-4">dialpad</mat-icon>
                                </button>
                                <button mat-flat-button color="warn" class="flex-1" (click)="hangupCall()">
                                    <mat-icon class="icon-size-4 mr-1">call_end</mat-icon>
                                    End
                                </button>
                            </div>
                            
                            <!-- DTMF Dialpad -->
                            @if (showDialpad) {
                                <div class="mt-3">
                                    <div class="text-center text-sm font-mono tracking-widest min-h-6 text-green-700 dark:text-green-300 mb-2">
                                        {{ dtmfInput || '&nbsp;' }}
                                    </div>
                                    <div class="grid grid-cols-3 gap-1.5 max-w-48 mx-auto">
                                        @for (key of dialpadKeys; track key.digit) {
                                            <button 
                                                class="flex flex-col items-center justify-center h-11 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-colors active:bg-gray-200 dark:active:bg-gray-500 cursor-pointer"
                                                (click)="onDialpadPress(key.digit)">
                                                <span class="text-base font-semibold leading-none">{{ key.digit }}</span>
                                                @if (key.letters) {
                                                    <span class="text-[9px] text-secondary leading-none mt-0.5 tracking-wider">{{ key.letters }}</span>
                                                }
                                            </button>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }

                    <!-- ==================== CALLS TAB ==================== -->
                    @if (activeTab === 'calls') {
                        <!-- Dialer - Make a call -->
                        @if (!activeCall && !incomingCall) {
                            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                                <div class="flex items-center gap-3">
                                    <div class="flex-1 relative">
                                        <input 
                                            type="text"
                                            [(ngModel)]="dialNumber"
                                            [matAutocomplete]="contactAuto"
                                            (input)="onContactSearch($event)"
                                            (keyup.enter)="makeCall()"
                                            placeholder="Name, phone, or email..."
                                            class="w-full px-4 py-3 pr-11 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                        <mat-autocomplete #contactAuto="matAutocomplete"
                                            (optionSelected)="onContactSelected($event)"
                                            [displayWith]="displayContact"
                                            class="voice-contact-autocomplete">
                                            @for (contact of contactResults; track contact.email || contact.phone) {
                                                <mat-option [value]="contact">
                                                    <div class="flex items-center gap-2.5 py-1">
                                                        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold uppercase"
                                                             [ngClass]="[getAvatarColor(contact.name || contact.email).bg, getAvatarColor(contact.name || contact.email).text]">
                                                            {{ getAvatarInitial(contact.name || contact.email) }}
                                                        </div>
                                                        <div class="flex flex-col min-w-0">
                                                            <span class="font-medium text-base truncate">{{ contact.name || contact.email }}</span>
                                                            @if (contact.phone) {
                                                                <span class="text-sm text-secondary truncate">{{ contact.phone }}</span>
                                                            }
                                                            @if (contact.email) {
                                                                <span class="text-sm text-secondary truncate">{{ contact.email }}</span>
                                                            }
                                                            @if (contact.company) {
                                                                <span class="text-xs text-secondary truncate">{{ contact.company }}</span>
                                                            }
                                                        </div>
                                                    </div>
                                                </mat-option>
                                            }
                                            @if (isSearchingContacts) {
                                                <mat-option disabled>
                                                    <span class="text-secondary text-base">Searching...</span>
                                                </mat-option>
                                            }
                                        </mat-autocomplete>
                                        @if (dialNumber) {
                                            <button 
                                                mat-icon-button 
                                                class="absolute right-0 top-0 h-full w-11"
                                                (click)="clearDialer()"
                                                matTooltip="Clear">
                                                <mat-icon class="icon-size-5 text-gray-400" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                                            </button>
                                        }
                                    </div>
                                    <button 
                                        mat-fab 
                                        color="primary"
                                        [disabled]="!dialNumber || dialNumber.length < 3"
                                        (click)="makeCall()"
                                        matTooltip="Call">
                                        <mat-icon [svgIcon]="'heroicons_solid:phone'"></mat-icon>
                                    </button>
                                </div>
                            </div>
                        }
                        
                        <!-- Call History -->
                        <div class="divide-y">
                            @for (call of callHistory; track call.id) {
                                <div class="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-black/5">
                                    <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                                         [ngClass]="getCallIconBgClass(call)">
                                        <mat-icon class="icon-size-4"
                                                  [ngClass]="getCallIconTextClass(call)"
                                                  [svgIcon]="getCallIcon(call)"></mat-icon>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        @if (call.callerName) {
                                            <div class="font-medium truncate">{{ call.callerName }}</div>
                                            <div class="text-xs text-secondary truncate">{{ formatPhoneNumber(call.direction === 'outgoing' ? call.to : call.from) }}</div>
                                        } @else {
                                            <div class="font-medium truncate">
                                                {{ formatPhoneNumber(call.direction === 'outgoing' ? call.to : call.from) }}
                                            </div>
                                        }
                                        <div class="text-xs text-secondary flex items-center gap-1">
                                            <span [class.text-red-500]="call.status === 'missed'">
                                                {{ getCallStatusLabel(call) }}
                                            </span>
                                            @if (call.duration) {
                                                <span>&middot; {{ formatDuration(call.duration) }}</span>
                                            }
                                        </div>
                                    </div>
                                    <div class="text-xs text-secondary">
                                        {{ call.timestamp | date: 'shortTime' }}
                                    </div>
                                    <button mat-icon-button 
                                            class="h-8 w-8 min-h-8"
                                            (click)="openSms(call)"
                                            [matTooltip]="'Send SMS'">
                                        <mat-icon class="icon-size-4 text-green-600">sms</mat-icon>
                                    </button>
                                    <button mat-icon-button 
                                            class="h-8 w-8 min-h-8"
                                            (click)="callBack(call)"
                                            [matTooltip]="'Call back'">
                                        <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:phone'"></mat-icon>
                                    </button>
                                </div>
                            }
                            
                            @if (callHistory.length === 0 && !incomingCall && !activeCall) {
                                <div class="flex flex-col items-center justify-center py-12 px-6">
                                    <div class="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-800">
                                        <mat-icon class="text-primary-600 dark:text-primary-200" [svgIcon]="'heroicons_outline:phone'"></mat-icon>
                                    </div>
                                    <div class="mt-4 text-lg font-semibold">No recent calls</div>
                                    <div class="mt-1 text-sm text-secondary text-center">
                                        @if (isRegistered) {
                                            Your call history will appear here
                                        } @else {
                                            Connecting to voice service...
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }

                    <!-- ==================== VOICEMAILS TAB ==================== -->
                    @if (activeTab === 'voicemails') {
                        <!-- Mark all read button -->
                        @if (unreadVoicemailCount > 0) {
                            <div class="flex items-center justify-end px-4 pt-3 pb-1">
                                <button class="text-xs text-primary hover:underline cursor-pointer" (click)="markAllRead()">
                                    Mark all as read
                                </button>
                            </div>
                        }

                        <div class="divide-y">
                            @for (vm of voicemails; track vm.id) {
                                <div class="flex flex-col gap-2 p-4 hover:bg-gray-50 dark:hover:bg-black/5 transition-colors"
                                     [ngClass]="{'bg-primary-50 dark:bg-primary-900/10': !vm.is_read}">
                                    <!-- Top row: caller info + time + actions -->
                                    <div class="flex items-center gap-3">
                                        <!-- Voicemail icon -->
                                        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                                             [ngClass]="!vm.is_read ? 'bg-primary-100 dark:bg-primary-800' : 'bg-gray-100 dark:bg-gray-700'">
                                            <mat-icon class="icon-size-4"
                                                      [ngClass]="!vm.is_read ? 'text-primary-600 dark:text-primary-300' : 'text-gray-500'">voicemail</mat-icon>
                                        </div>

                                        <!-- Caller + duration -->
                                        <div class="flex-1 min-w-0">
                                            @if (vm.callerName) {
                                                <div class="font-medium truncate" [class.font-semibold]="!vm.is_read">{{ vm.callerName }}</div>
                                                <div class="text-xs text-secondary truncate">{{ formatPhoneNumber(vm.from_number) }}</div>
                                            } @else {
                                                <div class="font-medium truncate" [class.font-semibold]="!vm.is_read">
                                                    {{ formatPhoneNumber(vm.from_number) }}
                                                </div>
                                            }
                                            <div class="text-xs text-secondary">
                                                {{ formatDuration(vm.duration) }}
                                            </div>
                                        </div>

                                        <!-- Date/time -->
                                        <div class="text-xs text-secondary shrink-0">
                                            {{ vm.created_at | date: 'M/d/yy, h:mm a' }}
                                        </div>

                                        <!-- Actions -->
                                        <button mat-icon-button 
                                                class="h-8 w-8 min-h-8"
                                                (click)="callBackVoicemail(vm)"
                                                [matTooltip]="'Call back'">
                                            <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:phone'"></mat-icon>
                                        </button>
                                        <button mat-icon-button 
                                                class="h-8 w-8 min-h-8"
                                                (click)="deleteVoicemail(vm)"
                                                [matTooltip]="'Delete'">
                                            <mat-icon class="icon-size-4 text-red-500" [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                                        </button>
                                    </div>

                                    <!-- Transcription text (if available) -->
                                    @if (vm.transcription) {
                                        <div class="text-sm text-secondary italic pl-11 pr-2 leading-snug">
                                            &ldquo;{{ vm.transcription }}&rdquo;
                                        </div>
                                    }

                                    <!-- Audio player -->
                                    @if (vm.recording_url) {
                                        <div class="pl-11 pr-2">
                                            <audio 
                                                controls 
                                                preload="none"
                                                class="w-full h-8"
                                                [src]="vm.recording_url"
                                                (play)="onVoicemailPlay(vm)"
                                                style="max-height: 32px;">
                                            </audio>
                                        </div>
                                    }
                                </div>
                            }
                            
                            <!-- Empty state -->
                            @if (voicemails.length === 0) {
                                <div class="flex flex-col items-center justify-center py-12 px-6">
                                    <div class="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-800">
                                        <mat-icon class="text-primary-600 dark:text-primary-200">voicemail</mat-icon>
                                    </div>
                                    <div class="mt-4 text-lg font-semibold">No voicemails</div>
                                    <div class="mt-1 text-sm text-secondary text-center">
                                        When callers leave a message, it will appear here
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
        </ng-template>
    `,
    styles: [`
        /* Host renders as an inline-flex shrink-to-fit shell so the
           pill button below can size to its own content, but the
           component still collapses cleanly when there's no
           voiceNumber and the @if guard renders nothing. */
        :host {
            display: inline-flex;
            align-items: center;
            flex: 0 0 auto;
        }
        :host:empty {
            display: none;
        }

        /* ───── Pill trigger ───────────────────────────────────────
           A self-contained 44 px rounded rectangle that holds the
           phone glyph + the formatted Twilio number on one line.
           No mat-icon-button base, so no Material width/aspect-ratio
           rules to fight. Hard anti-wrap guards (min-width: max-
           content, flex-wrap: nowrap, white-space: nowrap, flex:
           0 0 auto on each child) prevent the live-feed strip's
           flex-1 from squeezing the pill below its content width. */
        .voice-calls-pill {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: flex-start;
            flex: 0 0 auto;
            flex-wrap: nowrap;
            gap: 8px;
            min-width: max-content;
            width: auto;
            height: 44px;
            padding: 0 14px 0 12px;
            margin: 0;
            border-radius: 12px;
            border: 1px solid rgba(16, 185, 129, 0.28);
            background: rgba(16, 185, 129, 0.08);
            color: rgba(16, 185, 129, 0.95);
            font: inherit;
            white-space: nowrap;
            line-height: 1;
            cursor: pointer;
            outline: 0;
            -webkit-appearance: none;
            appearance: none;
            transition:
                background-color 0.18s ease,
                border-color 0.18s ease,
                transform 0.18s ease;
        }
        .voice-calls-pill:hover {
            background: rgba(16, 185, 129, 0.16);
            border-color: rgba(16, 185, 129, 0.42);
        }
        .voice-calls-pill--video {
            gap: 10px;
            height: 52px;
            padding-right: 8px;
            color: #dbeafe;
            background: linear-gradient(135deg, rgba(37, 99, 235, 0.94), rgba(79, 70, 229, 0.94));
            border-color: rgba(147, 197, 253, 0.8);
            box-shadow: 0 8px 24px rgba(37, 99, 235, 0.32);
            animation: video-pill-ring 1.25s ease-in-out infinite;
        }
        .voice-calls-pill__video-copy {
            display: flex;
            min-width: 96px;
            flex-direction: column;
            line-height: 1.1;
        }
        .voice-calls-pill__video-copy strong {
            overflow: hidden;
            max-width: 130px;
            font-size: 12px;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        .voice-calls-pill__video-copy small {
            margin-top: 3px;
            color: rgba(219, 234, 254, 0.82);
            font-size: 10px;
        }
        .voice-calls-pill__video-action {
            display: grid;
            width: 32px;
            height: 32px;
            place-items: center;
            color: #fff;
            border: 0;
            border-radius: 50%;
            cursor: pointer;
        }
        .voice-calls-pill__video-action mat-icon {
            width: 18px;
            height: 18px;
            font-size: 18px;
        }
        .voice-calls-pill__video-action.is-decline {
            background: #dc334b;
        }
        .voice-calls-pill__video-action.is-answer {
            background: #159a68;
        }
        @keyframes video-pill-ring {
            0%, 100% { box-shadow: 0 8px 24px rgba(37, 99, 235, 0.28); }
            50% { box-shadow: 0 8px 30px rgba(96, 165, 250, 0.58); }
        }
        .voice-calls-pill:active {
            transform: translateY(1px);
        }
        .voice-calls-pill:focus-visible {
            outline: 2px solid rgba(16, 185, 129, 0.55);
            outline-offset: 2px;
        }

        .voice-calls-pill__icon {
            flex: 0 0 auto;
            width: 18px !important;
            height: 18px !important;
            font-size: 18px !important;
            line-height: 18px !important;
            color: inherit;
        }

        .voice-calls-pill__number {
            flex: 0 0 auto;
            display: inline-block;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.02em;
            color: inherit;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
            line-height: 1;
        }

        /* Missed-call / unread-voicemail count badge — pinned to the
           top-right corner of the pill so it can't push the icon
           around inside the pill row. */
        .voice-calls-pill__badge {
            position: absolute;
            top: -6px;
            right: -6px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 1;
        }
        .voice-calls-pill__badge--count {
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            border-radius: 9999px;
            background: rgb(220, 38, 38);
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            line-height: 1;
        }
        .voice-calls-pill__badge--pulse {
            width: 10px;
            height: 10px;
            border-radius: 9999px;
            animation: voice-calls-pill-pulse 1.6s ease-in-out infinite;
        }
        .voice-calls-pill__badge--pulse.is-active {
            background: rgb(34, 197, 94);
        }
        .voice-calls-pill__badge--pulse.is-incoming {
            background: rgb(234, 179, 8);
        }
        @keyframes voice-calls-pill-pulse {
            0%, 100% { opacity: 1;   transform: scale(1); }
            50%      { opacity: 0.6; transform: scale(0.85); }
        }
    `]
})
export class VoiceCallsComponent implements OnInit, OnDestroy {
    @ViewChild('voiceCallsOrigin') private _voiceCallsOrigin: ElementRef<HTMLButtonElement>;
    @ViewChild('voiceCallsPanel') private _voiceCallsPanel: TemplateRef<any>;

    private voiceService = inject(VoiceService);
    private videoCallService = inject(VideoCallService);
    private _quickChatService = inject(QuickChatService);
    private _contactSearch = inject(ContactSearchService);
    private _overlay = inject(Overlay);
    private _viewContainerRef = inject(ViewContainerRef);
    private _changeDetectorRef = inject(ChangeDetectorRef);

    private _overlayRef: OverlayRef;
    private _unsubscribeAll = new Subject<void>();
    private durationSubscription: Subscription | null = null;
    videoCallState: VideoCallState = { state: 'idle' };

    missedCallCount = 0;
    incomingCall: VoiceCall | null = null;
    activeCall: VoiceCall | null = null;
    callHistory: CallHistoryEntry[] = [];
    isRegistered = false;
    callDuration = '00:00';
    dialNumber = '';
    voiceNumber: string | null = null;
    sharedLine = false;
    activeTab: 'calls' | 'voicemails' = 'calls';
    voicemails: VoicemailEntry[] = [];
    unreadVoicemailCount = 0;
    showDialpad = false;
    dtmfInput = '';
    contactResults: EmailContact[] = [];
    isSearchingContacts = false;
    private _contactSearchSubject = new Subject<string>();
    dialpadKeys: { digit: string; letters: string }[] = [
        { digit: '1', letters: '' },
        { digit: '2', letters: 'ABC' },
        { digit: '3', letters: 'DEF' },
        { digit: '4', letters: 'GHI' },
        { digit: '5', letters: 'JKL' },
        { digit: '6', letters: 'MNO' },
        { digit: '7', letters: 'PQRS' },
        { digit: '8', letters: 'TUV' },
        { digit: '9', letters: 'WXYZ' },
        { digit: '*', letters: '' },
        { digit: '0', letters: '+' },
        { digit: '#', letters: '' },
    ];

    ngOnInit(): void {
        this.videoCallService.state$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                this.videoCallState = state;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to missed call count
        this.voiceService.missedCallCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(count => {
                this.missedCallCount = count;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to incoming calls - auto-open panel when call comes in
        this.voiceService.incomingCall$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(call => {
                this.incomingCall = call;
                // Auto-open panel when incoming call arrives
                if (call) {
                    setTimeout(() => this.openPanel(), 100);
                }
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to active calls
        this.voiceService.activeCall$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(call => {
                this.activeCall = call;
                if (call?.status === 'connected') {
                    this.startDurationTimer(call);
                } else {
                    this.stopDurationTimer();
                    this.showDialpad = false;
                    this.dtmfInput = '';
                }
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to call history
        this.voiceService.callHistory$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(history => {
                this.callHistory = history;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to registration status
        this.voiceService.isRegistered$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(isRegistered => {
                this.isRegistered = isRegistered;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to voice config for phone number
        this.voiceService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => {
                this.voiceNumber = config?.voice_number || null;
                this.sharedLine = config?.shared_line ?? false;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to voicemails
        this.voiceService.voicemails$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(voicemails => {
                this.voicemails = voicemails;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to unread voicemail count
        this.voiceService.unreadVoicemailCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(count => {
                this.unreadVoicemailCount = count;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to panel open requests (e.g., when making a call from order card)
        this.voiceService.openPanelRequest$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((req) => {
                if (req?.dialNumber) {
                    this.dialNumber = req.dialNumber;
                    this._changeDetectorRef.markForCheck();
                }
                // Small delay to ensure ViewChild is ready
                setTimeout(() => this.openPanel(), 100);
            });

        // Contact search with debounce
        this._contactSearchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(query => {
                if (!query || query.trim().length < 2) {
                    return of([]);
                }
                this.isSearchingContacts = true;
                return this._contactSearch.searchContacts(query, 10);
            }),
            takeUntil(this._unsubscribeAll)
        ).subscribe(contacts => {
            this.contactResults = contacts;
            this.isSearchingContacts = false;
            this._changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.stopDurationTimer();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    openPanel(attempt = 0): void {
        if (!this._voiceCallsPanel || !this._voiceCallsOrigin) {
            // Origin lives inside @if (voiceNumber); retry briefly after config lands.
            if (attempt < 8) {
                setTimeout(() => this.openPanel(attempt + 1), 100);
            }
            return;
        }

        // Don't open if already open
        if (this._overlayRef?.hasAttached()) {
            return;
        }

        // Clear missed call count when opening panel
        this.voiceService.clearMissedCalls();
        
        // Refresh call history from Twilio
        this.voiceService.refreshCallHistory();
        
        // Refresh voicemails
        this.voiceService.refreshVoicemails();

        if (!this._overlayRef) {
            this._createOverlay();
        }

        this._overlayRef.attach(
            new TemplatePortal(this._voiceCallsPanel, this._viewContainerRef)
        );
    }

    closePanel(): void {
        this._overlayRef?.detach();
    }

    answerCall(): void {
        this.voiceService.answerCall();
    }

    declineCall(): void {
        this.voiceService.declineCall();
    }

    answerVideoCall(): void {
        void this.videoCallService.acceptCall();
    }

    declineVideoCall(): void {
        void this.videoCallService.declineCall();
    }

    hangupCall(): void {
        this.voiceService.hangupCall();
        this.showDialpad = false;
        this.dtmfInput = '';
    }

    onDialpadPress(digit: string): void {
        this.voiceService.sendDtmf(digit);
        this.dtmfInput += digit;
    }

    toggleMute(): void {
        this.voiceService.toggleMute();
    }

    callBack(call: CallHistoryEntry): void {
        const number = call.direction === 'outgoing' ? call.to : call.from;
        this.voiceService.makeCall(number);
    }

    openSms(call: CallHistoryEntry): void {
        const number = call.direction === 'outgoing' ? call.to : call.from;
        // Close the voice panel first
        this.closePanel();
        // Open SMS chat with the phone number
        this._quickChatService.startChatWithPhone(number);
    }

    displayContact(value: any): string {
        if (!value) return '';
        if (typeof value === 'string') return value;
        return value.phone || value.name || value.email || '';
    }

    onContactSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value || '';
        this._contactSearchSubject.next(value.trim());
    }

    onContactSelected(event: MatAutocompleteSelectedEvent): void {
        const contact = event.option.value as EmailContact;
        if (!contact) return;

        if (contact.phone) {
            this.dialNumber = contact.phone;
        }
        this.contactResults = [];
        this._changeDetectorRef.markForCheck();
    }

    clearDialer(): void {
        this.dialNumber = '';
        this.contactResults = [];
    }

    makeCall(): void {
        if (this.dialNumber && this.dialNumber.length >= 3) {
            this.voiceService.makeCall(this.dialNumber);
            this.dialNumber = '';
            this.contactResults = [];
        }
    }

    /** When a voicemail audio starts playing, mark it as read */
    onVoicemailPlay(vm: VoicemailEntry): void {
        if (!vm.is_read) {
            this.voiceService.markVoicemailRead(vm.id);
        }
    }

    /** Call back a voicemail sender */
    callBackVoicemail(vm: VoicemailEntry): void {
        this.voiceService.makeCall(vm.from_number);
    }

    /** Delete a voicemail */
    deleteVoicemail(vm: VoicemailEntry): void {
        this.voiceService.deleteVoicemail(vm.id);
    }

    /** Mark all voicemails as read */
    markAllRead(): void {
        this.voiceService.markAllVoicemailsRead();
    }

    formatPhoneNumber(phone: string): string {
        if (!phone) return 'Unknown';
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11 && digits.startsWith('1')) {
            return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        return phone;
    }

    formatDuration(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    private static readonly LETTER_COLORS: Record<string, { bg: string; text: string }> = {
        A: { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-200' },
        B: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' },
        C: { bg: 'bg-cyan-100 dark:bg-cyan-900', text: 'text-cyan-800 dark:text-cyan-200' },
        D: { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-800 dark:text-indigo-200' },
        E: { bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-800 dark:text-emerald-200' },
        F: { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900', text: 'text-fuchsia-800 dark:text-fuchsia-200' },
        G: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200' },
        H: { bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-800 dark:text-amber-200' },
        I: { bg: 'bg-indigo-100 dark:bg-indigo-900', text: 'text-indigo-800 dark:text-indigo-200' },
        J: { bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-800 dark:text-teal-200' },
        K: { bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-800 dark:text-amber-200' },
        L: { bg: 'bg-lime-100 dark:bg-lime-900', text: 'text-lime-800 dark:text-lime-200' },
        M: { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-800 dark:text-purple-200' },
        N: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' },
        O: { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-800 dark:text-orange-200' },
        P: { bg: 'bg-pink-100 dark:bg-pink-900', text: 'text-pink-800 dark:text-pink-200' },
        Q: { bg: 'bg-violet-100 dark:bg-violet-900', text: 'text-violet-800 dark:text-violet-200' },
        R: { bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-800 dark:text-rose-200' },
        S: { bg: 'bg-sky-100 dark:bg-sky-900', text: 'text-sky-800 dark:text-sky-200' },
        T: { bg: 'bg-teal-100 dark:bg-teal-900', text: 'text-teal-800 dark:text-teal-200' },
        U: { bg: 'bg-violet-100 dark:bg-violet-900', text: 'text-violet-800 dark:text-violet-200' },
        V: { bg: 'bg-violet-100 dark:bg-violet-900', text: 'text-violet-800 dark:text-violet-200' },
        W: { bg: 'bg-cyan-100 dark:bg-cyan-900', text: 'text-cyan-800 dark:text-cyan-200' },
        X: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-800 dark:text-slate-200' },
        Y: { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200' },
        Z: { bg: 'bg-zinc-100 dark:bg-zinc-800', text: 'text-zinc-800 dark:text-zinc-200' },
    };

    private static readonly DEFAULT_AVATAR_COLOR = { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300' };

    getAvatarInitial(name: string | null | undefined): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        const first = (parts[0]?.charAt(0) || '').toUpperCase();
        const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) || '').toUpperCase() : '';
        const initials = first + last;
        return initials || '#';
    }

    getAvatarColor(name: string | null | undefined): { bg: string; text: string } {
        if (!name) return VoiceCallsComponent.DEFAULT_AVATAR_COLOR;
        const letter = name.charAt(0).toUpperCase();
        return VoiceCallsComponent.LETTER_COLORS[letter] || VoiceCallsComponent.DEFAULT_AVATAR_COLOR;
    }

    getCallIcon(call: CallHistoryEntry): string {
        switch (call.status) {
            case 'missed':
            case 'declined':
            case 'failed':
            case 'no-answer':
                return 'heroicons_solid:phone-x-mark';
            case 'answered':
                return 'heroicons_solid:phone-arrow-down-left';
            case 'outgoing':
                return 'heroicons_solid:phone-arrow-up-right';
            case 'busy':
                return 'heroicons_solid:phone';
            default:
                return 'heroicons_solid:phone';
        }
    }

    getCallStatusLabel(call: CallHistoryEntry): string {
        switch (call.status) {
            case 'missed':
                return 'Missed call';
            case 'answered':
                return 'Incoming';
            case 'outgoing':
                return 'Outgoing';
            case 'declined':
                return 'Declined';
            case 'busy':
                return 'Busy';
            case 'failed':
                return 'Failed';
            case 'no-answer':
                return 'No answer';
            default:
                return call.status || 'Unknown';
        }
    }

    getCallIconBgClass(call: CallHistoryEntry): string {
        switch (call.status) {
            case 'missed':
            case 'failed':
            case 'no-answer':
                return 'bg-red-100 dark:bg-red-900/30';
            case 'answered':
                return 'bg-green-100 dark:bg-green-900/30';
            case 'outgoing':
                return 'bg-blue-100 dark:bg-blue-900/30';
            case 'declined':
            case 'busy':
                return 'bg-gray-100 dark:bg-gray-700';
            default:
                return 'bg-gray-100 dark:bg-gray-700';
        }
    }

    getCallIconTextClass(call: CallHistoryEntry): string {
        switch (call.status) {
            case 'missed':
            case 'failed':
            case 'no-answer':
                return 'text-red-600 dark:text-red-400';
            case 'answered':
                return 'text-green-600 dark:text-green-400';
            case 'outgoing':
                return 'text-blue-600 dark:text-blue-400';
            case 'declined':
            case 'busy':
                return 'text-gray-600 dark:text-gray-400';
            default:
                return 'text-gray-600 dark:text-gray-400';
        }
    }

    private startDurationTimer(call: VoiceCall): void {
        this.stopDurationTimer();
        const startTime = call.startTime || new Date();

        this.durationSubscription = interval(1000).subscribe(() => {
            const diff = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
            const mins = Math.floor(diff / 60);
            const secs = diff % 60;
            this.callDuration = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            this._changeDetectorRef.markForCheck();
        });
    }

    private stopDurationTimer(): void {
        if (this.durationSubscription) {
            this.durationSubscription.unsubscribe();
            this.durationSubscription = null;
        }
        this.callDuration = '00:00';
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._voiceCallsOrigin.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
